import { EmptyState } from "@/components/common/EmptyState";
import { ScreenHeader } from "@/components/common/ScreenHeader";
import { LoyaltyBadges } from "@/components/loyalty-badges";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Loader } from "@/components/ui/loader";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { getApiErrorMessage } from "@/lib/api";
import leaderboardService, {
  LeaderboardEntry,
} from "@/lib/services/leaderboard";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useQuery } from "@tanstack/react-query";
import { router } from "expo-router";
import React from "react";
import {
  FlatList,
  RefreshControl,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function LeaderboardScreen() {
  const { user, theme } = useAuth();
  const {
    data,
    isLoading,
    isRefetching,
    error: loadError,
    refetch,
  } = useQuery({
    queryKey: ["leaderboard"],
    queryFn: () => leaderboardService.getLeaderboard(),
  });

  const entries = data?.data ?? [];
  const current = data?.current;
  const error = loadError
    ? getApiErrorMessage(loadError, "Failed to load leaderboard")
    : null;

  const renderItem = ({ item }: { item: LeaderboardEntry }) => {
    const isCurrentUser = item.id === user?.id;

    return (
      <View
        style={[
          styles.row,
          { borderBottomColor: theme.border },
          isCurrentUser && styles.currentUserRow,
        ]}
      >
        <ThemedText
          style={styles.rank}
          lightColor={BrandColors.darkGray}
          darkColor={BrandColors.mediumGray}
        >
          {item.rank}
        </ThemedText>
        <View style={styles.rowBody}>
          <ThemedText style={styles.name} numberOfLines={1}>
            {item.name}
          </ThemedText>
          <ThemedText style={styles.points} type="extraSmall">
            {item.loyalty_points.toLocaleString()} points
          </ThemedText>
          <LoyaltyBadges isTopRated={item.is_top_rated} />
        </View>
        <LoyaltyBadges loyaltyTier={item.loyalty_tier} />
      </View>
    );
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader title="Leaderboard" />

      {isLoading ? (
        <Loader inline />
      ) : error ? (
        <View style={styles.centerContent}>
          <ThemedText style={styles.errorText} type="small">
            {error}
          </ThemedText>
          <BrandButton onPress={() => refetch()} style={styles.retry}>
            Retry
          </BrandButton>
        </View>
      ) : (
        <FlatList
          data={entries}
          renderItem={renderItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={isRefetching}
              onRefresh={() => refetch()}
            />
          }
          ListHeaderComponent={
            <>
              <View
                style={[styles.periodRow, { borderBottomColor: theme.border }]}
              >
                <ThemedText type="small">All time</ThemedText>
                <ThemedText style={styles.mutedText} type="extraSmall">
                  Pull down to refresh
                </ThemedText>
              </View>
              {current ? (
                <TouchableOpacity
                  style={[styles.summary, { borderBottomColor: theme.border }]}
                  onPress={() => router.push("/rewards")}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel="View reputation and rewards"
                >
                  <View style={styles.summaryBody}>
                    <ThemedText style={styles.mutedText} type="extraSmall">
                      YOUR RANK
                    </ThemedText>
                    <View style={styles.summaryPointsRow}>
                      <ThemedText style={styles.summaryPoints}>
                        {current.loyalty_points.toLocaleString()}
                      </ThemedText>
                      <ThemedText style={styles.mutedText} type="extraSmall">
                        points
                      </ThemedText>
                    </View>
                    <LoyaltyBadges
                      loyaltyTier={current.loyalty_tier}
                      isTopRated={current.is_top_rated}
                    />
                  </View>
                  <View style={styles.summaryRankRow}>
                    <ThemedText style={styles.summaryRank}>
                      #{current.rank}
                    </ThemedText>
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={BrandColors.mediumGray}
                    />
                  </View>
                </TouchableOpacity>
              ) : null}
            </>
          }
          ListFooterComponent={
            entries.length > 0 ? (
              <ThemedText
                style={[styles.mutedText, styles.footnote]}
                type="extraSmall"
              >
                Points come from completed bookings and referrals. Top Rated is
                based on customer ratings, not points.
              </ThemedText>
            ) : null
          }
          ListEmptyComponent={
            <EmptyState
              icon="trophy-outline"
              title="No operators yet"
              subtitle="Completed jobs will appear on the leaderboard."
            />
          }
        />
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContent: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
  },
  errorText: {
    textAlign: "center",
    color: BrandColors.mediumGray,
  },
  retry: {
    marginTop: 16,
  },
  listContent: {
    paddingHorizontal: 20,
    paddingBottom: 24,
    flexGrow: 1,
  },
  mutedText: {
    color: BrandColors.mediumGray,
  },
  periodRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
    borderBottomWidth: 1,
  },
  summary: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
    paddingVertical: 20,
    borderBottomWidth: 1,
    marginBottom: 16,
  },
  summaryBody: {
    flex: 1,
    gap: 6,
  },
  summaryPointsRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 6,
  },
  summaryPoints: {
    fontSize: 22,
    lineHeight: 28,
    fontFamily: "PlusJakartaSansBold",
  },
  summaryRankRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  summaryRank: {
    fontSize: 40,
    lineHeight: 48,
    fontFamily: "PlusJakartaSansBold",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    paddingHorizontal: 12,
    paddingVertical: 18,
    borderBottomWidth: 1,
  },
  currentUserRow: {
    backgroundColor: `${BrandColors.primary}1F`,
  },
  rank: {
    width: 20,
    fontSize: 16,
    textAlign: "center",
  },
  rowBody: {
    flex: 1,
    gap: 4,
  },
  name: {
    fontSize: 15,
    fontFamily: "PlusJakartaSansBold",
  },
  points: {
    color: BrandColors.mediumGray,
  },
  footnote: {
    marginTop: 16,
  },
});
